// AdminWordsPage.tsx — (B107; planning 10-23 16:45, 18:05; Creative 2's C2.9-1) Admin → Words: every word a customer
// meets, editable. The groups down the left; the keys with their current words, the default under an edit, the owner's
// state and a lock for a locked meaning; a panel beside the list edits one key (hints only: the workers' guards decide,
// and their answer is shown word for word), resets it to the default, and shows its last 20 changes.
import { useCallback, useEffect, useRef, useState } from "react";
import { PageContainer, TfCard, Btn } from "@/components/v2/primitives.tsx";
import { useAuthStore } from "@/stores/authStore.ts";
import { wordsApi } from "@/api/words.ts";
import { groupsOf, filterWords, lengthOf, chipsOf, oneLine, previewOf, saveOffer, ownerNote, answerText, readErrorText, historyLine,
  LOCK_NOTE, SAVED, RESET_DONE, NOTHING_TO_RESET, type WordView, type WordDetail, type WordFilter } from "@/lib/words-admin.ts";

const field = { display: "block", width: "100%", marginTop: 4 } as const;
const when = (iso: string) => iso.slice(0, 16).replace("T", " ");

function Lock() {
  return <span role="img" aria-label={LOCK_NOTE} title={LOCK_NOTE}>🔒</span>;
}

function EditPanel({ wordKey, isOwner, onChanged }: { wordKey: string; isOwner: boolean; onChanged: (gone?: boolean) => void }) {
  const [d, setD] = useState<WordDetail | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [text, setText] = useState("");
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [answer, setAnswer] = useState<{ ok: boolean; words: string } | null>(null);
  const shown = useRef(wordKey);   // (audit 10-23) the key on screen: a slower read of the key before it never lands
  shown.current = wordKey;
  const read = useCallback(async () => {
    try { const r = await wordsApi.get(wordKey); if (shown.current !== wordKey) return; setD(r); setText(r.text); setErr(null); }
    catch (e) { if (shown.current === wordKey) setErr(readErrorText(e)); }   // catch-reason: shown in place of the panel
  }, [wordKey]);
  useEffect(() => { setD(null); setErr(null); setAnswer(null); setReason(""); void read(); }, [read]);
  if (err && !d) return <TfCard><div style={{ color: "#b91c1c" }}>{err}</div></TfCard>;
  if (!d) return <TfCard><div className="tf-muted">Reading…</div></TfCard>;
  const len = lengthOf(text, d.maxLength), { chips, unknown } = chipsOf(text, d.placeholders), offer = saveOffer(d, text, reason), note = ownerNote(d, isOwner);
  const act = async (fn: () => Promise<{ changed: boolean }>, done: string, none: string) => {
    setBusy(true); setAnswer(null);
    try { const r = await fn(); setAnswer({ ok: true, words: r.changed ? done : none }); setReason(""); await read(); onChanged(); }
    catch (e) {   // catch-reason: the server's answer is shown in place; nothing changed
      setAnswer({ ok: false, words: answerText(e, wordKey) });
      if ((e as { status?: number }).status === 404) onChanged(true);
    } finally { setBusy(false); }
  };
  const save = () => act(() => wordsApi.save(wordKey, text, reason), SAVED, "Nothing changed: these are the words already shown.");
  const reset = () => {
    if (!window.confirm(`Back to the default? The words become: “${d.defaultText}”`)) return;
    void act(() => wordsApi.reset(wordKey, reason), RESET_DONE, NOTHING_TO_RESET);
  };
  return (
    <TfCard>
      <h3 style={{ marginTop: 0, wordBreak: "break-all" }}>{d.key}</h3>
      <div className="tf-muted" style={{ fontSize: 12 }}>Shows on: {d.shows}</div>
      {d.lockedMeaning && <div style={{ fontSize: 12, color: "#92400e", marginTop: 4 }}><Lock /> {LOCK_NOTE}</div>}
      {d.editRefused && <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 4 }}>The saved edit no longer passes its checks ({d.editRefused}), so the default shows.</div>}
      <label style={{ display: "block", marginTop: 8 }}>The words
        <textarea style={field} rows={d.multiline ? 6 : 2} value={text}
          onKeyDown={(e) => { if (e.key === "Enter" && !d.multiline) e.preventDefault(); }}
          onChange={(e) => setText(oneLine(e.target.value, d.multiline))} /></label>
      <div style={{ fontSize: 12, marginTop: 2, color: len.over ? "#b91c1c" : undefined }}>{len.label}</div>
      {(chips.length > 0 || unknown.length > 0) && <div style={{ display: "flex", flexWrap: "wrap", gap: 4, marginTop: 4, fontSize: 12 }}>
        {chips.map((c) => <span key={c.name} style={{ padding: "1px 6px", borderRadius: 10, background: c.present ? "#dcfce7" : "#fee2e2" }}>
          {c.present ? "✓" : "✗"} &lt;{c.name}&gt;{c.present ? "" : " missing"}</span>)}
        {unknown.map((n) => <span key={n} style={{ padding: "1px 6px", borderRadius: 10, background: "#fef3c7" }}>&lt;{n}&gt; not a placeholder of this key</span>)}
      </div>}
      <div style={{ fontSize: 12, marginTop: 8 }}>Preview: <span style={{ whiteSpace: "pre-wrap" }}>{previewOf(text, d.placeholders)}</span></div>
      <label style={{ display: "block", marginTop: 8 }}>The reason{d.lockedMeaning ? " (required)" : " (optional)"}
        <input style={field} maxLength={300} value={reason} onChange={(e) => setReason(e.target.value)} /></label>
      {note && <div className="tf-muted" style={{ fontSize: 12, marginTop: 6 }}>{note}</div>}
      {!offer.ok && <div style={{ fontSize: 12, color: "#92400e", marginTop: 4 }}>{offer.why}</div>}
      {answer && <div style={{ fontSize: 13, marginTop: 6, color: answer.ok ? "#166534" : "#b91c1c" }}>{answer.words}</div>}
      <div style={{ display: "flex", gap: 8, marginTop: 10 }}>
        <Btn onClick={save} disabled={busy || !offer.ok}>{busy ? "Saving…" : "Save"}</Btn>
        {d.edit && <Btn variant="ghost" onClick={reset} disabled={busy || (d.lockedMeaning && !reason.trim())}>Back to the default</Btn>}
      </div>
      <h4 style={{ marginBottom: 4 }}>History</h4>
      {d.history.length === 0 ? <div className="tf-muted" style={{ fontSize: 12 }}>No changes yet: the default words.</div>
        : <ul style={{ fontSize: 12, paddingLeft: 16, margin: 0 }}>{d.history.slice(0, 20).map((h) => (
          <li key={h.id} style={{ marginBottom: 4 }}>{when(h.decidedAt)} · {h.decidedBy} ({h.decidedAs}{h.overApproved ? ", over the owner's approval" : ""}): {historyLine(h)}
            {h.reason ? <span className="tf-muted"> — {h.reason}</span> : null}</li>))}</ul>}
    </TfCard>
  );
}

export function AdminWordsPage() {
  const isOwner = useAuthStore((s) => s.user?.role === "superadmin");
  const [words, setWords] = useState<WordView[] | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [f, setF] = useState<WordFilter>({ search: "", group: null, edited: false, locked: false, proposed: false });
  const [open, setOpen] = useState<string | null>(null);
  const load = useCallback(async () => {
    try { setWords((await wordsApi.list()).words); setErr(null); }
    catch (e) { setErr(readErrorText(e)); }   // catch-reason: shown in place of the list
  }, []);
  useEffect(() => { void load(); }, [load]);
  const rows = words ? filterWords(words, f) : [];
  const toggle = (k: "edited" | "locked" | "proposed", label: string) =>
    <label style={{ fontSize: 12, marginRight: 10 }}><input type="checkbox" checked={f[k]} onChange={() => setF({ ...f, [k]: !f[k] })} /> {label}</label>;
  return (
    <PageContainer title="Words" subtitle="Every word a customer meets on the engine's pages: reword one here; the checks keep its meaning and its placeholders.">
      {err && !words && <TfCard><div style={{ color: "#b91c1c" }}>The words couldn't be read: {err}</div><Btn size="sm" variant="ghost" onClick={() => void load()}>Read again</Btn></TfCard>}
      {!words && !err && <TfCard><div className="tf-muted">Reading…</div></TfCard>}
      {words && <div style={{ display: "grid", gridTemplateColumns: "160px minmax(0, 1fr) minmax(0, 380px)", gap: 12, alignItems: "start" }}>
        <TfCard>
          <div style={{ fontSize: 13 }}>
            <div><button style={{ fontWeight: f.group === null ? 700 : 400 }} onClick={() => setF({ ...f, group: null })}>All ({words.length})</button></div>
            {groupsOf(words).map((g) => <div key={g.group}><button style={{ fontWeight: f.group === g.group ? 700 : 400 }} onClick={() => setF({ ...f, group: g.group })}>{g.group} ({g.count})</button></div>)}
          </div>
        </TfCard>
        <TfCard>
          <input style={{ width: "100%" }} placeholder="Search the key, the default or the current words" aria-label="Search the words" value={f.search} onChange={(e) => setF({ ...f, search: e.target.value })} />
          <div style={{ marginTop: 6 }}>{toggle("edited", "Edited only")}{toggle("locked", "Locked only")}{toggle("proposed", "Proposed only")}</div>
          <table style={{ width: "100%", fontSize: 12, marginTop: 8, borderCollapse: "collapse" }}>
            <thead><tr style={{ textAlign: "left" }}><th>Key</th><th>Shows on</th><th>The words</th><th>State</th></tr></thead>
            <tbody>{rows.map((v) => (
              <tr key={v.key} style={{ borderTop: "1px solid #e5e7eb", background: open === v.key ? "#eef2ff" : undefined }}>
                <td style={{ wordBreak: "break-all" }}>{v.lockedMeaning && <Lock />} <button aria-label={`Edit ${v.key}`} onClick={() => setOpen(v.key)}
                  style={{ background: "none", border: 0, padding: 0, color: "inherit", textDecoration: "underline", cursor: "pointer", textAlign: "left", wordBreak: "break-all" }}>{v.key}</button></td>
                <td>{v.shows}</td>
                <td>{v.text}{v.edit && <div className="tf-muted">Edited by {v.edit.decidedBy}, {when(v.edit.decidedAt)} · the default: {v.defaultText}</div>}</td>
                <td>{v.state === "edited_by_operator" ? "edited by an operator" : v.state}</td>
              </tr>))}</tbody>
          </table>
          {rows.length === 0 && <div className="tf-muted" style={{ fontSize: 12, marginTop: 8 }}>No words match.</div>}
        </TfCard>
        {open ? <EditPanel wordKey={open} isOwner={isOwner} onChanged={(gone) => { if (gone) setOpen(null); void load(); }} />
          : <TfCard><div className="tf-muted" style={{ fontSize: 13 }}>Pick a key to reword it.</div></TfCard>}
      </div>}
    </PageContainer>
  );
}
